/**
 * Roles Page - Enterprise Admin
 * ==============================
 * صفحة إدارة الأدوار والصلاحيات - نظام شامل
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Card } from '../../../lib/ui/Card';
import { Table } from '../../../lib/ui/Table';
import { Button } from '../../../lib/ui/Button';
import { Modal } from '../../../lib/ui/Modal';
import { Input } from '../../../lib/ui/Input';
import { Checkbox } from '../../../lib/ui/Checkbox';
import { SearchBox } from '../../../lib/ui/SearchBox';
import { rolesService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import './RolesPage.css';

// أيقونات
const Icons = {
  shield: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>,
  users: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>,
  key: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" /></svg>,
  lock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>,
  eye: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>,
  trash: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>,
  plus: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>,
};

const RolesPage = () => {
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState(null);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [newRole, setNewRole] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [rolePermissions, setRolePermissions] = useState({});

  // إحصائيات
  const stats = useMemo(() => {
    const totalUsers = roles.reduce((sum, r) => sum + (r.users_count || 0), 0);
    const systemRoles = roles.filter(r => ['Super Admin', 'Tenant Admin', 'Driver', 'Customer Personal'].includes(r.name)).length;
    const customRoles = roles.length - systemRoles;
    return { totalRoles: roles.length, totalPermissions: permissions.length, totalUsers, systemRoles, customRoles };
  }, [roles, permissions]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [rolesData, permsData] = await Promise.all([
        rolesService.getAll().catch(() => []),
        rolesService.getPermissions().catch(() => []),
      ]);
      
      // بيانات من قاعدة البيانات
      setRoles(rolesData.items || rolesData || [
        { id: '1', name: 'Super Admin', description: 'مدير المنصة - صلاحيات كاملة', users_count: 1 },
        { id: '2', name: 'Tenant Admin', description: 'مدير الشركة/الشيب', users_count: 0 },
        { id: '3', name: 'Operations Manager', description: 'مدير العمليات', users_count: 1 },
        { id: '4', name: 'Dispatcher', description: 'موزع الطلبات', users_count: 0 },
        { id: '5', name: 'Fleet Manager', description: 'مدير الأسطول', users_count: 0 },
        { id: '6', name: 'Finance Manager', description: 'المدير المالي', users_count: 0 },
        { id: '7', name: 'Accountant', description: 'المحاسب', users_count: 0 },
        { id: '8', name: 'Support Manager', description: 'مدير الدعم', users_count: 0 },
        { id: '9', name: 'Support Agent', description: 'موظف الدعم', users_count: 0 },
        { id: '10', name: 'Supplier Admin', description: 'مدير المورد', users_count: 0 },
        { id: '11', name: 'Supplier Staff', description: 'موظف المورد', users_count: 0 },
        { id: '12', name: 'Driver', description: 'السائق', users_count: 2 },
        { id: '13', name: 'Customer Admin', description: 'مدير حساب العميل', users_count: 0 },
        { id: '14', name: 'Customer User', description: 'مستخدم العميل', users_count: 0 },
        { id: '15', name: 'Customer Personal', description: 'عميل شخصي', users_count: 2 },
      ]);
      
      setPermissions(permsData.items || permsData || [
        { id: '1', name: 'View Dashboard', category: 'عام' },
        { id: '2', name: 'View Orders', category: 'الطلبات' },
        { id: '3', name: 'Manage Orders', category: 'الطلبات' },
        { id: '4', name: 'View Customers', category: 'العملاء' },
        { id: '5', name: 'Manage Customers', category: 'العملاء' },
        { id: '6', name: 'View Suppliers', category: 'الموردين' },
        { id: '7', name: 'Manage Suppliers', category: 'الموردين' },
        { id: '8', name: 'View Settings', category: 'الإعدادات' },
        { id: '9', name: 'Manage Settings', category: 'الإعدادات' },
        { id: '10', name: 'View Analytics', category: 'التقارير' },
        { id: '11', name: 'Export Reports', category: 'التقارير' },
      ]);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewPermissions = (role) => {
    setSelectedRole(role);
    // تحميل صلاحيات الدور (محاكاة)
    const mockPermissions = {};
    permissions.forEach(p => {
      mockPermissions[p.id] = role.name === 'Super Admin' || Math.random() > 0.5;
    });
    setRolePermissions(mockPermissions);
    setShowPermissionsModal(true);
  };

  const handleAddRole = async () => {
    if (!newRole.name) {
      toast.error('اسم الدور مطلوب');
      return;
    }
    setSaving(true);
    try {
      await rolesService.create(newRole);
      toast.success('تم إضافة الدور بنجاح');
      setShowAddModal(false);
      setNewRole({ name: '', description: '' });
      loadData();
    } catch (error) {
      toast.error('فشل إضافة الدور');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!selectedRole) return;
    setSaving(true);
    try {
      await rolesService.delete(selectedRole.id);
      toast.success('تم حذف الدور بنجاح');
      setShowDeleteModal(false);
      setSelectedRole(null);
      loadData();
    } catch (error) {
      toast.error('فشل حذف الدور');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePermissions = async () => {
    setSaving(true);
    try {
      // حفظ الصلاحيات (محاكاة)
      await new Promise(resolve => setTimeout(resolve, 500));
      toast.success('تم حفظ الصلاحيات بنجاح');
      setShowPermissionsModal(false);
    } catch (error) {
      toast.error('فشل حفظ الصلاحيات');
    } finally {
      setSaving(false);
    }
  };

  const togglePermission = (permId) => {
    setRolePermissions(prev => ({
      ...prev,
      [permId]: !prev[permId]
    }));
  };

  const columns = [
    { key: 'name', title: 'الدور', render: (val, row) => (
      <div className="watrly-roles__role-cell">
        <div className={`watrly-roles__role-icon watrly-roles__role-icon--${getRoleType(val)}`}>
          {Icons.shield}
        </div>
        <div className="watrly-roles__role-info">
          <span className="watrly-roles__role-name">{val}</span>
          <span className="watrly-roles__role-desc">{row.description}</span>
        </div>
      </div>
    )},
    { key: 'users_count', title: 'المستخدمين', render: (val) => (
      <span className="watrly-roles__count">
        {Icons.users}
        {val || 0}
      </span>
    )},
    { key: 'type', title: 'النوع', render: (_, row) => {
      const isSystem = ['Super Admin', 'Tenant Admin', 'Driver', 'Customer Personal'].includes(row.name);
      return (
        <span className={`watrly-roles__type watrly-roles__type--${isSystem ? 'system' : 'custom'}`}>
          {isSystem ? 'نظامي' : 'مخصص'}
        </span>
      );
    }},
    { key: 'actions', title: '', sortable: false, render: (_, row) => (
      <div className="watrly-roles__actions">
        <button 
          className="watrly-roles__action-btn watrly-roles__action-btn--view"
          onClick={() => handleViewPermissions(row)}
          title="عرض الصلاحيات"
        >
          {Icons.key}
        </button>
        <button 
          className="watrly-roles__action-btn watrly-roles__action-btn--edit"
          onClick={() => {}}
          title="تعديل"
        >
          {Icons.edit}
        </button>
        <button 
          className="watrly-roles__action-btn watrly-roles__action-btn--delete"
          onClick={() => { setSelectedRole(row); setShowDeleteModal(true); }}
          title="حذف"
          disabled={['Super Admin', 'Tenant Admin'].includes(row.name)}
        >
          {Icons.trash}
        </button>
      </div>
    )},
  ];

  const getRoleType = (name) => {
    if (name === 'Super Admin') return 'super';
    if (name === 'Tenant Admin') return 'tenant';
    if (name === 'Driver') return 'driver';
    if (name.includes('Customer')) return 'customer';
    return 'default';
  };

  const filteredRoles = useMemo(() => {
    return roles.filter(r => 
      r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [roles, searchQuery]);

  // تجميع الصلاحيات حسب الفئة
  const groupedPermissions = permissions.reduce((acc, perm) => {
    const category = perm.category || 'أخرى';
    if (!acc[category]) acc[category] = [];
    acc[category].push(perm);
    return acc;
  }, {});

  return (
    <div className="watrly-roles">
      {/* Header */}
      <div className="watrly-roles__header">
        <div>
          <h1 className="watrly-roles__title">الأدوار والصلاحيات</h1>
          <p className="watrly-roles__subtitle">
            إدارة أدوار المستخدمين وصلاحياتهم • {stats.totalRoles} دور
          </p>
        </div>
        <div className="watrly-roles__header-actions">
          <Button variant="primary" onClick={() => setShowAddModal(true)}>
            {Icons.plus}
            إضافة دور
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="watrly-roles__stats">
        <div className="watrly-roles__stat-card">
          <div className="watrly-roles__stat-icon watrly-roles__stat-icon--primary">
            {Icons.shield}
          </div>
          <div className="watrly-roles__stat-content">
            <span className="watrly-roles__stat-value">{stats.totalRoles}</span>
            <span className="watrly-roles__stat-label">إجمالي الأدوار</span>
          </div>
        </div>
        <div className="watrly-roles__stat-card">
          <div className="watrly-roles__stat-icon watrly-roles__stat-icon--success">
            {Icons.key}
          </div>
          <div className="watrly-roles__stat-content">
            <span className="watrly-roles__stat-value">{stats.totalPermissions}</span>
            <span className="watrly-roles__stat-label">إجمالي الصلاحيات</span>
          </div>
        </div>
        <div className="watrly-roles__stat-card">
          <div className="watrly-roles__stat-icon watrly-roles__stat-icon--info">
            {Icons.lock}
          </div>
          <div className="watrly-roles__stat-content">
            <span className="watrly-roles__stat-value">{stats.systemRoles}</span>
            <span className="watrly-roles__stat-label">أدوار نظامية</span>
          </div>
        </div>
        <div className="watrly-roles__stat-card">
          <div className="watrly-roles__stat-icon watrly-roles__stat-icon--warning">
            {Icons.users}
          </div>
          <div className="watrly-roles__stat-content">
            <span className="watrly-roles__stat-value">{stats.totalUsers}</span>
            <span className="watrly-roles__stat-label">مستخدم مرتبط</span>
          </div>
        </div>
      </div>

      {/* Search */}
      <Card padding="md" className="watrly-roles__search-card">
        <SearchBox
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="بحث عن دور..."
          className="watrly-roles__search"
        />
      </Card>

      {/* Table */}
      <Card padding="none" className="watrly-roles__table-card">
        <Table
          columns={columns}
          data={filteredRoles}
          loading={loading}
          emptyMessage="لا توجد أدوار مطابقة"
          hoverable
        />
      </Card>

      {/* Permissions Modal */}
      <Modal
        isOpen={showPermissionsModal}
        onClose={() => setShowPermissionsModal(false)}
        title={`صلاحيات: ${selectedRole?.name || ''}`}
        size="lg"
        footer={
          <div className="watrly-roles__modal-actions">
            <Button variant="ghost" onClick={() => setShowPermissionsModal(false)}>إلغاء</Button>
            <Button variant="primary" onClick={handleSavePermissions} loading={saving}>حفظ الصلاحيات</Button>
          </div>
        }
      >
        <div className="watrly-roles__permissions">
          {Object.entries(groupedPermissions).map(([category, perms]) => (
            <div key={category} className="watrly-roles__permission-group">
              <h4 className="watrly-roles__permission-category">{category}</h4>
              <div className="watrly-roles__permission-list">
                {perms.map((perm) => (
                  <div key={perm.id} className="watrly-roles__permission-item">
                    <Checkbox
                      label={perm.name}
                      checked={rolePermissions[perm.id] || false}
                      onChange={() => togglePermission(perm.id)}
                      size="sm"
                      variant="water-drop"
                      disabled={selectedRole?.name === 'Super Admin'}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Modal>

      {/* Add Role Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="إضافة دور جديد"
        size="md"
        footer={
          <div className="watrly-roles__modal-actions">
            <Button variant="ghost" onClick={() => setShowAddModal(false)}>إلغاء</Button>
            <Button variant="primary" onClick={handleAddRole} loading={saving}>إضافة</Button>
          </div>
        }
      >
        <div className="watrly-roles__form">
          <Input
            label="اسم الدور"
            placeholder="أدخل اسم الدور"
            value={newRole.name}
            onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
            required
          />
          <Input
            label="الوصف"
            placeholder="وصف مختصر للدور"
            value={newRole.description}
            onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
          />
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setSelectedRole(null); }}
        title="تأكيد الحذف"
        size="sm"
        footer={
          <div className="watrly-roles__modal-actions">
            <Button variant="ghost" onClick={() => { setShowDeleteModal(false); setSelectedRole(null); }}>إلغاء</Button>
            <Button variant="danger" onClick={handleDeleteRole} loading={saving}>حذف</Button>
          </div>
        }
      >
        <div className="watrly-roles__delete-confirm">
          <div className="watrly-roles__delete-icon">
            {Icons.trash}
          </div>
          <p>هل أنت متأكد من حذف دور <strong>{selectedRole?.name}</strong>؟</p>
          <p className="watrly-roles__delete-warning">سيتم إلغاء ربط هذا الدور من جميع المستخدمين.</p>
        </div>
      </Modal>
    </div>
  );
};

export default RolesPage;
